import * as React from "react";
import { useDropzone } from "react-dropzone";
import {
  Button,
  Dialog,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  DialogTrigger,
  makeStyles,
  MessageBar,
  MessageBarActions,
  MessageBarBody,
  Popover,
  PopoverSurface,
  PopoverTrigger,
  Image,
} from "@fluentui/react-components";
import { CloudArrowUpRegular, DeleteRegular, ImageRegular, MoreHorizontal24Filled } from "@fluentui/react-icons";

const useStyles = makeStyles({
  dropZone: {
    display: "flex",
    flexDirection: "column",
    justifyItems: "center",
    alignItems: "center",
    gap: "5px",
    border: "2px dashed #eeeeee",
    backgroundColor: "#fafafa",
    color: "#bdbdbd",
    borderRadius: "4px",
    padding: "20px",
    textAlign: "center",
    outline: "none",
    transition: "border .24s ease-in-out",
  },
});

const Message = ({ file, setFile }) => {
  return (
    <MessageBar key={file.name} shape="square" intent="success" icon={<ImageRegular />}>
      <MessageBarBody>{file.name}</MessageBarBody>
      <MessageBarActions
        containerAction={
          <Popover size="small">
            <PopoverTrigger disableButtonEnhancement>
              <Button aria-label="dismiss" appearance="transparent" icon={<MoreHorizontal24Filled />} />
            </PopoverTrigger>
            <PopoverSurface tabIndex={-1}>
              <div className="flex flex-col items-start gap-[8px]">
                <Dialog modalType="non-modal">
                  <DialogTrigger disableButtonEnhancement>
                    <Button as="button" appearance="subtle" icon={<ImageRegular />}>
                      Preview file
                    </Button>
                  </DialogTrigger>
                  <DialogSurface style={{ minWidth: 320, maxWidth: 400 }}>
                    <DialogBody>
                      <DialogTitle>{file.name}</DialogTitle>
                      <DialogContent>
                        <Image src={file.preview} alt={file.name} width={300} height={300} shape="rounded" />
                      </DialogContent>
                    </DialogBody>
                  </DialogSurface>
                </Dialog>
                <Button as="button" appearance="subtle" icon={<DeleteRegular />} onClick={() => setFile(null)}>
                  Remove file
                </Button>
              </div>
            </PopoverSurface>
          </Popover>
        }
      ></MessageBarActions>
    </MessageBar>
  );
};

export default function FileUpload({ file, setFile }) {
  const styles = useStyles();

  const onDrop = React.useCallback((acceptedFiles, _rejectedFiles) => {
    if (acceptedFiles?.length) {
      const f = acceptedFiles[0];
      setFile(Object.assign(f, { preview: URL.createObjectURL(f) }));
    }
  }, []);

  // React.useEffect(() => {
  //   // Revoke the data uris to avoid memory leaks
  //   return () => files.forEach((file) => URL.revokeObjectURL(file.preview));
  // }, [files]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    accept: {
      "image/jpeg": [],
      "image/png": [],
    },
    onDrop,
    maxSize: 3 * 1024 * 1024,
    multiple: false,
    maxFiles: 1,
  });

  return (
    <div className="flex flex-col gap-[10px]">
      <div
        {...getRootProps({
          className: styles.dropZone,
        })}
      >
        <input type="button" {...getInputProps()} />
        {isDragActive ? (
          <p>Drop the files here ...</p>
        ) : (
          <div>
            <CloudArrowUpRegular fontSize={24} />
            <p>Drag and drop a file here, or click to select file</p>
            <em>(Only a *.jpeg or a *.png image will be accepted)</em>
          </div>
        )}
      </div>

      {file && <Message file={file} setFile={setFile} />}
    </div>
  );
}
